import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroupDirective, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { Validator } from '../common/validator';
import { VstaticImageService } from '../core/vstatic-image.service';
import { Customer } from '@vendasta/customer-voice-service';
import { ConfirmCustomerFormDialogComponent } from './confirm-dialog.component';
import { BusinessInfo, CustomerFormService, TEMPLATE_TYPE_SMS } from './customer-facing.service';

const SUPPORT_LANGUAGES = ['en', 'fr', 'fr-fr', 'fr-ca', 'cs', 'it', 'nl', 'de', 'es-419'];
const CUSTOMER_SOURCE_MOBILE_KIOSK = 'Mobile Kiosk';

@Component({
  selector: 'app-customer-facing',
  styleUrls: ['./customer-facing.component.scss'],
  templateUrl: './customer-facing.component.html',
  standalone: false,
})
export class CustomerFacingComponent implements OnInit, OnDestroy {
  @ViewChild(FormGroupDirective) customerForm: FormGroupDirective;
  businessId$: Observable<string>;
  form: UntypedFormGroup;
  confirmationChecked = false;
  businessInfo$: Observable<BusinessInfo>;
  defaultLogoUrl: string;
  templateType: string;
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  public isEnabled$: Observable<boolean>;
  subscriptions: Subscription[] = [];
  readonly TEMPLATE_TYPE_SMS = TEMPLATE_TYPE_SMS;

  constructor(
    private customerFormService: CustomerFormService,
    private formBuilder: UntypedFormBuilder,
    private imageService: VstaticImageService,
    private translateService: TranslateService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
  ) {
    this.translateService.setDefaultLang('en');
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      firstName: ['', Validator.isCustomerName],
      lastName: ['', Validator.isCustomerName],
      emailAddress: ['', Validator.isEmail],
      phoneNumber: ['', Validator.isPhoneNumber],
    });

    this.businessId$ = this.route.queryParamMap.pipe(
      take(1),
      map((paramMap: ParamMap) => paramMap.get('agid')),
    );
    this.route.queryParamMap.pipe(take(1)).subscribe((paramMap: ParamMap) => {
      const language = paramMap.get('lang');
      if (!!language && SUPPORT_LANGUAGES.includes(language)) {
        this.translateService.use(language);
      }
    });
    this.businessInfo$ = this.businessId$.pipe(
      switchMap((agid: string) => this.customerFormService.loadBusinessInfo(agid)),
      tap((businessInfo: BusinessInfo) => {
        this.loading$$.next(false);
        if (businessInfo) {
          this.templateType = businessInfo.templateType;
        }
      }),
      shareReplay(1),
    );
    this.defaultLogoUrl = this.imageService.getImageSrc('customer-facing-form/default-business-logo.png');
  }

  submit(): void {
    if (!this.form.valid) {
      return;
    }

    this.form.disable();
    this.subscriptions.push(
      combineLatest([this.businessId$, this.recaptchaV3Service.execute('submit')])
        .pipe(
          switchMap(([businessId, token]) => {
            const customer = new Customer({
              businessId: businessId,
              firstName: this.form.value.firstName,
              lastName: this.form.value.lastName,
              email: this.form.value.emailAddress,
              phone: this.form.value.phoneNumber,
              origin: CUSTOMER_SOURCE_MOBILE_KIOSK,
            });
            return this.customerFormService
              .createCustomerAndSendReviewRequest(customer, token)
              .pipe(finalize(() => this.form.enable()));
          }),
        )
        .subscribe(
          () => {
            this.dialog.open(ConfirmCustomerFormDialogComponent, {
              data: {
                templateType: this.templateType,
              },
            });
            this.resetFrom();
          },
          () =>
            this.snackbarService.errorSnack(
              this.translateService.instant('CUSTOMER_FACING_FORM.ERROR_SUBMITTING_FORM'),
            ),
        ),
    );
  }

  resetFrom(): void {
    this.confirmationChecked = false;
    this.customerForm.resetForm();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}
